import { useParams, useNavigate } from 'react-router-dom';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Tabs, TabPanel, useTabs } from '@/components/ui/Tabs';
import { useToast } from '@/components/ui/Toast';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useAuthStore } from '@/features/auth/hooks/useAuth';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import {
  useJobDetail,
  useCompleteTask,
  useCreateAdHocTask,
  useTransitionStage,
  useUpdateJob,
  useChangeJobStatus,
  useCancelJob,
  useAssignTaskToClient,
  useAllowedTransitions,
  type JobStatus,
  type JobPriority,
  type JobStage,
  type JobTask,
} from './hooks/useJobs';
import { JobGroupsTab } from './JobGroupsTab';
import { useJobNotes, useCreateJobNote } from './hooks/useJobNotes';
import { useNoteCategories } from '@/features/contacts/hooks/useNoteCategories';
import { useStaffUsers } from '@/features/contacts/hooks/useStaffUsers';
import {
  useResolveFormForEntityType,
  type FormSection,
} from '@/features/form-builder/hooks/useFormBuilder';

/** Map job status to display label */
const STATUS_LABELS: Record<JobStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  waiting_on_client: 'Waiting on Client',
  in_review: 'In Review',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

const STATUS_VARIANTS: Record<JobStatus, BadgeVariant> = {
  not_started: 'default',
  in_progress: 'info',
  on_hold: 'warning',
  waiting_on_client: 'warning',
  in_review: 'info',
  complete: 'success',
  cancelled: 'danger',
};

const PRIORITY_VARIANTS: Record<JobPriority, BadgeVariant> = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  urgent: 'danger',
};

/**
 * Job detail page showing stages, tasks, and timeline.
 * Validates: Requirements 8.8, 8.17
 */
export function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { activeTab, onChange: setActiveTab } = useTabs('');

  const { data: job, isLoading } = useJobDetail(jobId ?? '');
  const completeTask = useCompleteTask();
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);
  const [assigningTaskId, setAssigningTaskId] = useState<string | null>(null);
  const transitionStage = useTransitionStage();
  const updateJob = useUpdateJob();
  const changeJobStatus = useChangeJobStatus();
  const cancelJob = useCancelJob();
  const assignTaskToClient = useAssignTaskToClient();
  const { data: transitionsData } = useAllowedTransitions(jobId ?? '');

  // Load form definition for the job's entity type (dynamic fields rendering)
  const entityTypeId = job?.entityTypeId ?? '';
  const { data: formDefinition } = useResolveFormForEntityType(entityTypeId);

  // Auto-select first tab once form definition loads
  useEffect(() => {
    if (formDefinition) {
      const formTabs = (formDefinition.sections ?? []).filter(
        (s) => s.render_as === 'tab' && !s.parent_tab_id,
      );
      const hasOrphans = (formDefinition.sections ?? []).some(
        (s) => s.render_as !== 'tab' && !s.parent_tab_id && s.is_active,
      );
      if (formTabs.length > 0) {
        setActiveTab(`section-${formTabs[0].id}`);
      } else if (hasOrphans) {
        setActiveTab('form-details');
      } else {
        setActiveTab('details');
      }
    } else if (!activeTab) {
      setActiveTab('details');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDefinition]);

  // Fetch fresh user profile for job_role (auth store may be stale)
  const { data: currentUserProfile } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<{ jobRole: string | null }>>('/auth/me');
      return response.data.data;
    },
    staleTime: 60_000,
  });
  const currentUserJobRole =
    (currentUserProfile?.jobRole ?? useAuthStore.getState().user?.jobRole ?? null)?.toLowerCase() ??
    null;

  const [cancelReason, setCancelReason] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <svg
            className="mx-auto h-8 w-8 animate-spin text-brand-500"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-500">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="py-20 text-center">
        <p className="text-gray-500">Job not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/jobs')}>
          Back to Jobs
        </Button>
      </div>
    );
  }

  const sortedStages = [...(job.stages || [])].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );
  const activeStageIndex = sortedStages.findIndex((s) => s.status === 'active');
  // Determine current stage: prefer active stage, then match by job.currentStageId, then last completed
  const currentStageIndex =
    activeStageIndex >= 0
      ? activeStageIndex
      : sortedStages.findIndex((s) => s.id === job.currentStageId) >= 0
        ? sortedStages.findIndex((s) => s.id === job.currentStageId)
        : sortedStages.findIndex((s) => s.status === 'pending') >= 0
          ? sortedStages.findIndex((s) => s.status === 'pending')
          : sortedStages.length - 1;
  // Determine if there's a next stage after the current one
  const isOnFinalStage = currentStageIndex >= sortedStages.length - 1;
  const nextStage =
    !isOnFinalStage && currentStageIndex >= 0 ? sortedStages[currentStageIndex + 1] : null;

  // Check if all mandatory tasks in the current stage are completed
  const currentStage = currentStageIndex >= 0 ? sortedStages[currentStageIndex] : null;
  const currentStageTasks = (job.tasks || []).filter((t) => t.stageId === currentStage?.id);
  const mandatoryTasksCompleted = currentStageTasks
    .filter((t) => t.isMandatory)
    .every((t) => t.isCompleted);

  const handleCompleteTask = (taskId: string) => {
    if (!jobId) return;
    // Ignore clicks while a completion is already in flight to prevent
    // duplicate submissions / concurrent task completions.
    if (completingTaskId !== null || completeTask.isPending) return;
    setCompletingTaskId(taskId);
    completeTask.mutate(
      { jobId, taskId },
      {
        // The hook awaits the job-detail refetch inside onSuccess, so by the
        // time onSettled fires the refreshed task (is_completed=true) has
        // already landed. Clearing here therefore swaps the spinner straight
        // for a checked, disabled box — no lag window where an unchecked box
        // is re-enabled and clickable again.
        onSettled: () => setCompletingTaskId(null),
      },
    );
  };

  const handleTransitionStage = () => {
    if (!jobId || !nextStage) return;
    transitionStage.mutate({ jobId, targetStageId: nextStage.id });
  };

  const handleCancelJob = () => {
    if (!jobId || !cancelReason.trim()) return;
    cancelJob.mutate(
      {
        jobId,
        cancellationReason: cancelReason.trim(),
      },
      {
        onSuccess: () => {
          setShowCancelDialog(false);
          setCancelReason('');
          navigate('/jobs');
        },
      },
    );
  };

  // Build tabs from form definition (same as contacts)
  const allFormSections = formDefinition?.sections ?? [];
  const formTabSections = allFormSections.filter((s) => s.render_as === 'tab' && !s.parent_tab_id);
  const orphanFormSections = allFormSections.filter(
    (s) => s.render_as !== 'tab' && !s.parent_tab_id && s.is_active,
  );
  const hasOrphanSections = orphanFormSections.length > 0;

  const tabs = [
    // Form definition tabs (or a default "Details" for orphans)
    ...(formTabSections.length > 0
      ? formTabSections.map((s) => ({ id: `section-${s.id}`, label: s.name }))
      : hasOrphanSections
        ? [{ id: 'form-details', label: 'Details' }]
        : [{ id: 'details', label: 'Details' }]),
    { id: 'stages', label: 'Stages & Tasks' },
    ...(can(PERMISSIONS.NOTE_CATEGORIES_READ) ? [{ id: 'notes', label: 'Notes' }] : []),
    ...(can(PERMISSIONS.GROUPS_READ) ? [{ id: 'groups', label: 'Groups' }] : []),
    { id: 'timeline', label: 'Timeline' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/jobs')}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Back to jobs"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{job.title}</h1>
          </div>
          <div className="mt-2 flex items-center gap-3">
            {job.jobCode && (
              <span className="rounded bg-gray-100 px-2.5 py-0.5 font-mono text-sm font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                {job.jobCode}
              </span>
            )}
            {/* Show the current workflow STAGE as the user-facing status (badge
                colour still reflects the real status). Terminal statuses and
                jobs without a stage fall back to the status label. */}
            <Badge variant={STATUS_VARIANTS[job.status]} dot>
              {currentStage?.name && job.status !== 'complete' && job.status !== 'cancelled'
                ? currentStage.name
                : STATUS_LABELS[job.status]}
            </Badge>
            <Badge variant={PRIORITY_VARIANTS[job.priority || 'normal']} size="sm">
              {(job.priority || 'normal').charAt(0).toUpperCase() +
                (job.priority || 'normal').slice(1)}
            </Badge>
            {job.isOverdue && (
              <Badge variant="danger" size="sm">
                Overdue
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Job Status dropdown — only for users with change_status permission */}
          {can(PERMISSIONS.JOBS_CHANGE_STATUS) &&
            job.status !== 'complete' &&
            job.status !== 'cancelled' && (
              <select
                value={job.status}
                onChange={(e) => {
                  const newStatus = e.target.value as JobStatus;
                  if (newStatus !== job.status) {
                    changeJobStatus.mutate({ jobId: jobId!, status: newStatus });
                  }
                }}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                aria-label="Change job status"
              >
                <option value={job.status}>{STATUS_LABELS[job.status]}</option>
                {job.status !== 'on_hold' && <option value="on_hold">On Hold</option>}
                {job.status !== 'in_review' && <option value="in_review">In Review</option>}
              </select>
            )}
          {/* Template-driven transitions: show "Advance to Stage" buttons based on linked statuses */}
          {can(PERMISSIONS.JOBS_TRANSITION) &&
            transitionsData?.has_linked_statuses &&
            transitionsData.allowed_transitions.length > 0 &&
            job.status !== 'complete' &&
            job.status !== 'cancelled' && (
              <>
                {transitionsData.allowed_transitions.slice(0, 1).map((transition) => (
                  <Button
                    key={transition.stage_id}
                    onClick={handleTransitionStage}
                    loading={transitionStage.isPending}
                    disabled={!mandatoryTasksCompleted}
                    title={
                      !mandatoryTasksCompleted
                        ? 'Complete all mandatory tasks before advancing'
                        : undefined
                    }
                  >
                    {/* Advance targets the next STAGE in the queue. Prefer the
                        actual next stage name; fall back to the transition's
                        stage name, then its status label. */}
                    Advance to{' '}
                    {nextStage?.name || transition.stage_name || STATUS_LABELS[transition.status]}
                  </Button>
                ))}
              </>
            )}
          {/* Fallback: show default buttons when no linked statuses are configured */}
          {!transitionsData?.has_linked_statuses && (
            <>
              {can(PERMISSIONS.JOBS_UPDATE) && job.status === 'not_started' && (
                <Button
                  onClick={() => updateJob.mutate({ id: jobId!, status: 'in_progress' })}
                  loading={updateJob.isPending}
                >
                  Start Job
                </Button>
              )}
              {can(PERMISSIONS.JOBS_TRANSITION) &&
                nextStage &&
                job.status !== 'complete' &&
                job.status !== 'cancelled' &&
                job.status !== 'not_started' && (
                  <Button
                    onClick={handleTransitionStage}
                    loading={transitionStage.isPending}
                    disabled={!mandatoryTasksCompleted}
                    title={
                      !mandatoryTasksCompleted
                        ? 'Complete all mandatory tasks before advancing'
                        : undefined
                    }
                  >
                    Advance to {nextStage.name}
                  </Button>
                )}
            </>
          )}
          {can(PERMISSIONS.JOBS_UPDATE) &&
            job.status !== 'complete' &&
            job.status !== 'cancelled' && (
              <Button variant="outline" onClick={() => navigate(`/jobs/${jobId}/edit`)}>
                Edit Job
              </Button>
            )}
          {can(PERMISSIONS.JOBS_UPDATE) &&
            job.status !== 'cancelled' &&
            job.status !== 'complete' && (
              <Button variant="danger" onClick={() => setShowCancelDialog(true)}>
                Cancel Job
              </Button>
            )}
          {/* Reactivate cancelled job — admin only */}
          {currentUserJobRole === 'administrator' && job.status === 'cancelled' && (
            <Button
              variant="outline"
              onClick={() => updateJob.mutate({ id: jobId!, status: 'in_progress' })}
              loading={updateJob.isPending}
            >
              Reactivate Job
            </Button>
          )}
          {/* Complete Job button — shown on final stage when all mandatory tasks done */}
          {(can(PERMISSIONS.JOBS_TRANSITION) || can(PERMISSIONS.JOBS_UPDATE)) &&
            !nextStage &&
            sortedStages.length > 0 &&
            job.status !== 'complete' &&
            job.status !== 'cancelled' && (
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => updateJob.mutate({ id: jobId!, status: 'complete' })}
                loading={updateJob.isPending}
                disabled={!mandatoryTasksCompleted}
                title={
                  !mandatoryTasksCompleted
                    ? 'Complete all mandatory tasks before completing the job'
                    : undefined
                }
              >
                Complete Job
              </Button>
            )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card padding="sm">
          <div className="text-sm text-gray-500 dark:text-gray-400">Client</div>
          <div className="mt-1 font-medium text-gray-900 dark:text-gray-100">{job.clientName}</div>
        </Card>
        <Card padding="sm">
          <div className="text-sm text-gray-500 dark:text-gray-400">Job Type</div>
          <div className="mt-1 font-medium text-gray-900 dark:text-gray-100">{job.jobType}</div>
        </Card>
        <Card padding="sm">
          <div className="text-sm text-gray-500 dark:text-gray-400">Progress</div>
          <div className="mt-1 flex items-center gap-2">
            <div className="h-2 flex-1 rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-2 rounded-full bg-brand-500 transition-all"
                style={{ width: `${job.progressPercentage}%` }}
              />
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {job.progressPercentage}%
            </span>
          </div>
        </Card>
        <Card padding="sm">
          <div className="text-sm text-gray-500 dark:text-gray-400">Due Date</div>
          <div className="mt-1 font-medium text-gray-900 dark:text-gray-100">
            {job.dueDate ? new Date(job.dueDate).toLocaleDateString() : '—'}
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Card padding="none">
        <div className="px-6 pt-4">
          <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        </div>

        <div className="p-6">
          <TabPanel tabId="stages" activeTab={activeTab}>
            <StagesAndTasks
              stages={sortedStages}
              tasks={job.tasks}
              onCompleteTask={handleCompleteTask}
              completingTaskId={completingTaskId}
              assigningTaskId={assigningTaskId}
              onAssignToClient={(taskId, assigned, dueDate) => {
                setAssigningTaskId(taskId);
                assignTaskToClient.mutate(
                  { jobId: jobId!, taskId, assignedToClient: assigned, dueDate },
                  { onSettled: () => setAssigningTaskId(null) },
                );
              }}
              canUpdate={can(PERMISSIONS.JOBS_UPDATE)}
              canCreateTask={can(PERMISSIONS.JOBS_CREATE_TASK)}
              canAssignToClient={can(PERMISSIONS.JOBS_ASSIGN_TASK_TO_CLIENT)}
              jobId={jobId!}
              jobStatus={job.status}
              currentStageId={
                job.currentStageId ||
                (currentStageIndex >= 0 ? sortedStages[currentStageIndex].id : null)
              }
              currentUserJobRole={currentUserJobRole}
            />
          </TabPanel>

          <TabPanel tabId="timeline" activeTab={activeTab}>
            <JobActivityLog jobId={jobId!} />
          </TabPanel>

          {can(PERMISSIONS.NOTE_CATEGORIES_READ) && (
            <TabPanel tabId="notes" activeTab={activeTab}>
              <JobNotesTab jobId={jobId!} />
            </TabPanel>
          )}

          {can(PERMISSIONS.GROUPS_READ) && (
            <TabPanel tabId="groups" activeTab={activeTab}>
              <JobGroupsTab job={job} />
            </TabPanel>
          )}

          {/* Form definition tab panels */}
          {formTabSections.map((tab, tabIndex) => {
            const explicitChildren = allFormSections
              .filter((s) => s.parent_tab_id === tab.id && s.render_as !== 'tab' && s.is_active)
              .sort((a, b) => a.display_order - b.display_order);

            let implicitChildren: typeof allFormSections = [];
            if (explicitChildren.length === 0) {
              const nextTab = formTabSections[tabIndex + 1];
              implicitChildren = orphanFormSections.filter((s) => {
                const afterThisTab = s.display_order >= tab.display_order;
                const beforeNextTab = nextTab ? s.display_order < nextTab.display_order : true;
                return afterThisTab && beforeNextTab;
              });
            }

            const children = explicitChildren.length > 0 ? explicitChildren : implicitChildren;
            const tabHasFields = tab.fields.filter((f) => f.is_active).length > 0;
            const sectionsToRender = [
              ...(tabHasFields ? [{ ...tab, render_as: 'section' as const }] : []),
              ...children,
            ];

            return (
              <TabPanel key={tab.id} tabId={`section-${tab.id}`} activeTab={activeTab}>
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {sectionsToRender.map((section) => (
                    <JobDynamicDetailSection key={section.id} section={section} job={job} />
                  ))}
                </div>
              </TabPanel>
            );
          })}

          {/* Orphan sections (no tab sections defined) */}
          {formTabSections.length === 0 && hasOrphanSections && (
            <TabPanel tabId="form-details" activeTab={activeTab}>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {orphanFormSections.map((section) => (
                  <JobDynamicDetailSection key={section.id} section={section} job={job} />
                ))}
              </div>
            </TabPanel>
          )}

          {/* Fallback details (no form definition at all) */}
          {!formDefinition && (
            <TabPanel tabId="details" activeTab={activeTab}>
              <JobDetails job={job} />
            </TabPanel>
          )}
        </div>
      </Card>

      {/* Cancel dialog */}
      {showCancelDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-dialog-title"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-surface-dark-secondary">
            <h2
              id="cancel-dialog-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              Cancel Job
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Please provide a reason for cancelling this job. This action cannot be undone.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Cancellation reason..."
              className="mt-4 block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
              rows={3}
              required
              aria-label="Cancellation reason"
            />
            <div className="mt-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
                Keep Job
              </Button>
              <Button
                variant="danger"
                onClick={handleCancelJob}
                disabled={!cancelReason.trim()}
                loading={cancelJob.isPending}
              >
                Cancel Job
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Stages and tasks panel */
function StagesAndTasks({
  stages,
  tasks,
  onCompleteTask,
  completingTaskId,
  assigningTaskId,
  onAssignToClient,
  canUpdate,
  canCreateTask,
  canAssignToClient,
  jobId,
  jobStatus,
  currentStageId,
  currentUserJobRole,
}: {
  stages: JobStage[];
  tasks: JobTask[];
  onCompleteTask: (taskId: string) => void;
  completingTaskId?: string | null;
  assigningTaskId?: string | null;
  onAssignToClient: (taskId: string, assigned: boolean, dueDate?: string | null) => void;
  canUpdate: boolean;
  canCreateTask: boolean;
  canAssignToClient: boolean;
  jobId: string;
  jobStatus: JobStatus;
  currentStageId: string | null;
  currentUserJobRole: string | null;
}) {
  const isJobActive =
    jobStatus === 'not_started' ||
    jobStatus === 'in_progress' ||
    jobStatus === 'in_review' ||
    jobStatus === 'waiting_on_client' ||
    jobStatus === 'on_hold';
  // True while any task completion request is in flight — used to disable all
  // task checkboxes so a second task can't be toggled mid-request.
  const isCompletingAny = completingTaskId != null;
  const createAdHocTask = useCreateAdHocTask();
  const [showAddTaskForm, setShowAddTaskForm] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskMandatory, setNewTaskMandatory] = useState(false);
  const [newTaskType, setNewTaskType] = useState<'standard' | 'document_upload'>('standard');

  const resetAddTaskForm = () => {
    setShowAddTaskForm(false);
    setNewTaskTitle('');
    setNewTaskMandatory(false);
    setNewTaskType('standard');
  };

  const handleAddTask = () => {
    if (!newTaskTitle.trim()) return;
    createAdHocTask.mutate(
      {
        jobId,
        title: newTaskTitle.trim(),
        isMandatory: newTaskMandatory,
        taskType: newTaskType,
      },
      {
        onSuccess: () => {
          resetAddTaskForm();
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      {stages.map((stage, index) => {
        const stageTasks = (tasks || [])
          .filter((t) => t.stageId === stage.id)
          .sort((a, b) => a.displayOrder - b.displayOrder);

        const isCurrent = stage.id === currentStageId;
        const currentIndex = stages.findIndex((s) => s.id === currentStageId);
        const isPast = currentIndex >= 0 && index < currentIndex;
        const isFuture = currentIndex >= 0 && index > currentIndex;
        const isAdminRole = currentUserJobRole?.toLowerCase() === 'administrator';
        const isTasksEditable =
          canUpdate && ((isJobActive && isCurrent) || (isAdminRole && jobStatus === 'complete'));

        const mandatoryTasks = stageTasks.filter((t) => t.isMandatory);
        const mandatoryCompleted = mandatoryTasks.every((t) => t.isCompleted);
        const allCompleted = stageTasks.length > 0 && stageTasks.every((t) => t.isCompleted);

        let stageStatus: 'completed' | 'active' | 'pending' = 'pending';
        if (isPast || (isCurrent && allCompleted && stageTasks.length > 0)) {
          stageStatus = 'completed';
        } else if (isCurrent && stageTasks.length === 0 && jobStatus === 'complete') {
          stageStatus = 'completed';
        } else if (isCurrent) {
          stageStatus = 'active';
        }

        return (
          <div
            key={stage.id}
            className={`rounded-lg border p-4 transition-colors ${
              isCurrent
                ? 'border-brand-300 bg-brand-50/50 dark:border-brand-700 dark:bg-brand-900/10'
                : isPast
                  ? 'border-green-200 bg-green-50/30 dark:border-green-800 dark:bg-green-900/10'
                  : 'border-border opacity-60 dark:border-border-dark'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <StageStatusIcon status={stageStatus} />
                <h3
                  className={`font-medium ${isCurrent ? 'text-brand-700 dark:text-brand-300' : 'text-gray-900 dark:text-gray-100'}`}
                >
                  {stage.name}
                </h3>
                {isCurrent && (
                  <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-semibold text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                    Current
                  </span>
                )}
              </div>
              <Badge
                variant={
                  stageStatus === 'completed'
                    ? 'success'
                    : stageStatus === 'active'
                      ? 'info'
                      : 'default'
                }
                size="sm"
              >
                {stageStatus === 'completed'
                  ? 'Completed'
                  : stageStatus === 'active'
                    ? 'In Progress'
                    : 'Pending'}
              </Badge>
            </div>

            {/* Progress bar for current stage */}
            {isCurrent && stageTasks.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    {stageTasks.filter((t) => t.isCompleted).length} / {stageTasks.length} tasks
                    done
                  </span>
                  {!mandatoryCompleted && mandatoryTasks.length > 0 && (
                    <span className="text-amber-600 dark:text-amber-400">
                      {mandatoryTasks.filter((t) => !t.isCompleted).length} mandatory remaining
                    </span>
                  )}
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className="h-full rounded-full bg-brand-500 transition-all"
                    style={{
                      width: `${stageTasks.length > 0 ? (stageTasks.filter((t) => t.isCompleted).length / stageTasks.length) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {stageTasks.length > 0 && (
              <ul className="mt-4 space-y-2 pl-8" role="list">
                {stageTasks.map((task) => {
                  const roleRestricted =
                    !!task.assigneeRole &&
                    task.assigneeRole.toLowerCase() !== 'others' &&
                    task.assigneeRole.toLowerCase() !== 'none';
                  const isAdmin = currentUserJobRole?.toLowerCase() === 'administrator';
                  const roleMatches =
                    currentUserJobRole?.toLowerCase() === task.assigneeRole?.toLowerCase();
                  const roleBlocked = roleRestricted && !isAdmin && !roleMatches;

                  return (
                    <li key={task.id} className="flex items-start gap-3">
                      {task.taskType === 'document_upload' ? (
                        <DocumentUploadTask
                          task={task}
                          jobId={jobId}
                          disabled={!isTasksEditable || roleBlocked || isCompletingAny}
                          onCompleted={() => onCompleteTask(task.id)}
                          isAdmin={currentUserJobRole?.toLowerCase() === 'administrator'}
                          canAssignToClient={canAssignToClient && !isFuture}
                          onAssignToClient={onAssignToClient}
                          assigning={assigningTaskId === task.id}
                        />
                      ) : (
                        <>
                          {completingTaskId === task.id ? (
                            <svg
                              className="mt-0.5 h-4 w-4 animate-spin text-brand-600"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                          ) : (
                            <input
                              type="checkbox"
                              checked={task.isCompleted}
                              onChange={() =>
                                !task.isCompleted &&
                                isTasksEditable &&
                                !roleBlocked &&
                                !isCompletingAny &&
                                onCompleteTask(task.id)
                              }
                              disabled={
                                task.isCompleted ||
                                !isTasksEditable ||
                                roleBlocked ||
                                isCompletingAny
                              }
                              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              aria-label={`Mark "${task.title}" as complete`}
                              title={
                                roleBlocked ? `Requires "${task.assigneeRole}" role` : undefined
                              }
                            />
                          )}
                          <div className="flex-1">
                            <span
                              className={
                                task.isCompleted
                                  ? 'text-sm text-gray-400 line-through dark:text-gray-500'
                                  : isFuture
                                    ? 'text-sm text-gray-400 dark:text-gray-500'
                                    : 'text-sm text-gray-900 dark:text-gray-100'
                              }
                            >
                              {task.title}
                              {task.isMandatory && (
                                <span
                                  className="ml-1 text-xs text-danger-500"
                                  aria-label="mandatory"
                                >
                                  *
                                </span>
                              )}
                            </span>
                            {task.assigneeRole && (
                              <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                                ({task.assigneeRole})
                              </span>
                            )}
                            {task.assignedToName && (
                              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                — {task.assignedToName}
                              </span>
                            )}
                            {task.assignedToClient && (
                              <Badge variant="warning" size="sm" className="ml-2">
                                Waiting on Client
                              </Badge>
                            )}
                          </div>
                          {/* Assign to Client toggle */}
                          {canAssignToClient && !task.isCompleted && !isFuture && (
                            <div className="ml-2">
                              <AssignClientButton
                                assigned={task.assignedToClient}
                                busy={assigningTaskId === task.id}
                                currentDueDate={task.clientDueDate}
                                onAssign={(assigned, dueDate) =>
                                  onAssignToClient(task.id, assigned, dueDate)
                                }
                              />
                            </div>
                          )}
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Lock indicator for future stages */}
            {isFuture && stageTasks.length > 0 && (
              <p className="mt-3 pl-8 text-xs text-gray-400 dark:text-gray-500 italic">
                Complete the current stage to unlock these tasks
              </p>
            )}

            {/* Add Task button for current stage */}
            {isCurrent && canCreateTask && isJobActive && (
              <div className="mt-4 pl-8">
                {!showAddTaskForm ? (
                  <button
                    type="button"
                    onClick={() => setShowAddTaskForm(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:border-brand-400 hover:text-brand-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-brand-500 dark:hover:text-brand-400 transition-colors"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" />
                    </svg>
                    Add Task
                  </button>
                ) : (
                  <div className="flex flex-col gap-2 rounded-md border border-brand-200 bg-white p-3 dark:border-brand-700 dark:bg-gray-800">
                    <input
                      type="text"
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      placeholder="Task title"
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddTask();
                        if (e.key === 'Escape') {
                          resetAddTaskForm();
                        }
                      }}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <input
                            type="checkbox"
                            checked={newTaskMandatory}
                            onChange={(e) => setNewTaskMandatory(e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                          Mandatory
                        </label>
                        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <span>Type</span>
                          <select
                            value={newTaskType}
                            onChange={(e) =>
                              setNewTaskType(e.target.value as 'standard' | 'document_upload')
                            }
                            className="rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                            aria-label="Task type"
                          >
                            <option value="standard">Standard</option>
                            <option value="document_upload">Document Upload</option>
                          </select>
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={resetAddTaskForm}
                          className="rounded px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleAddTask}
                          disabled={!newTaskTitle.trim() || createAdHocTask.isPending}
                          className="rounded bg-brand-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                        >
                          {createAdHocTask.isPending ? 'Adding...' : 'Add'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Job not started message */}
      {jobStatus === 'not_started' && (
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
          Start the job to begin working on stages and tasks.
        </p>
      )}
    </div>
  );
}

/** Stage status icon */
function StageStatusIcon({ status }: { status: 'pending' | 'active' | 'completed' }) {
  if (status === 'completed') {
    return (
      <svg
        className="h-5 w-5 text-success-500"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  if (status === 'active') {
    return (
      <svg
        className="h-5 w-5 text-brand-500"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.25a.75.75 0 00-1.5 0v2.5h-2.5a.75.75 0 000 1.5h2.5v2.5a.75.75 0 001.5 0v-2.5h2.5a.75.75 0 000-1.5h-2.5v-2.5z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  return (
    <svg
      className="h-5 w-5 text-gray-300 dark:text-gray-600"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/** Activity log panel with scroll, search, and pagination */
function JobActivityLog({ jobId }: { jobId: string }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['job-activity', jobId, page, debouncedSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', '30');
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res = await apiClient.get<{
        success: boolean;
        data: Array<{
          id: string;
          action: string;
          entity_type: string;
          entity_id: string;
          user_id: string | null;
          user_name: string | null;
          metadata: Record<string, unknown> | null;
          new_value: Record<string, unknown> | null;
          previous_value: Record<string, unknown> | null;
          created_at: string;
        }>;
        pagination: { page: number; pageSize: number; total: number; totalPages: number };
      }>(`/jobs/${jobId}/activity?${params.toString()}`);
      return res.data;
    },
    enabled: !!jobId,
  });

  const entries = data?.data ?? [];
  const pagination = data?.pagination;

  // Pulls a free-text comment/reason off an entry wherever it may live.
  // Cancellations carry the reason as `cancellation_reason` on new_value
  // (PUT /jobs/:id and PATCH /jobs/:id/status both include it in the body).
  const getEntryComment = (entry: (typeof entries)[0]): string | null => {
    const nv = (entry.new_value ?? {}) as Record<string, unknown>;
    const md = (entry.metadata ?? {}) as Record<string, unknown>;
    const candidate =
      nv.cancellation_reason ?? md.cancellation_reason ?? nv.reason ?? md.reason ?? nv.comment;
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
    return null;
  };

  const formatDescription = (entry: (typeof entries)[0]): string => {
    const { action, entity_type, new_value, metadata } = entry;
    switch (entity_type) {
      // `status` is the entity_type the audit middleware derives for the
      // PATCH /jobs/:id/status route (last path segment = "status").
      case 'status':
      case 'job':
      case 'jobs': {
        if (action === 'create') return 'Job was created';
        if (action === 'update') {
          const status = (new_value as Record<string, unknown>)?.status as string;
          if (status === 'cancelled') return 'Job was cancelled';
          if (status) return `Status changed to ${status.replace(/_/g, ' ')}`;
          return 'Job details updated';
        }
        return `Job ${action}`;
      }
      case 'job_tasks':
      case 'job_task': {
        const title =
          (new_value as Record<string, unknown>)?.task_title ||
          (metadata as Record<string, unknown>)?.task_title ||
          'task';
        if (action === 'update') return `Task "${title}" completed`;
        return `Task "${title}" ${action}`;
      }
      case 'job_notes':
      case 'job_note': {
        const cat = (new_value as Record<string, unknown>)?.category as string;
        const label = cat ? cat.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : '';
        return label ? `Note added (${label})` : 'Note added';
      }
      case 'documents':
      case 'document': {
        const fname =
          (new_value as Record<string, unknown>)?.file_name ||
          (metadata as Record<string, unknown>)?.file_name ||
          'document';
        if (action === 'create') return `Document "${fname}" uploaded`;
        return `Document "${fname}" ${action}`;
      }
      default:
        return `${entity_type.replace(/_/g, ' ')} ${action}`;
    }
  };

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <svg
          className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search activity..."
          className="w-full rounded-lg border border-border bg-white py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-500 focus:outline-none dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
        />
      </div>

      {/* Activity list with scroll */}
      <div className="max-h-[500px] overflow-y-auto rounded-lg border border-border dark:border-border-dark">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
            <span className="ml-2 text-sm text-gray-500">Loading activity...</span>
          </div>
        ) : entries.length > 0 ? (
          <ul className="divide-y divide-border dark:divide-border-dark">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-surface-dark-secondary transition-colors"
              >
                <div className="flex items-start gap-3">
                  <ActivityIcon entityType={entry.entity_type} action={entry.action} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {formatDescription(entry)}
                    </p>
                    {getEntryComment(entry) && (
                      <p className="mt-1 whitespace-pre-wrap rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-700 dark:bg-surface-dark-tertiary dark:text-gray-300">
                        <span className="font-medium text-gray-500 dark:text-gray-400">
                          Comment:{' '}
                        </span>
                        {getEntryComment(entry)}
                      </p>
                    )}
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      {entry.user_name && <span>{entry.user_name}</span>}
                      {entry.user_name && <span>·</span>}
                      <time dateTime={entry.created_at}>
                        {new Date(entry.created_at).toLocaleString('en-CA', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </time>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {debouncedSearch ? 'No activity matching your search.' : 'No activity recorded yet.'}
          </p>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} entries)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded border border-border px-2 py-1 disabled:opacity-40 hover:bg-gray-50 dark:border-border-dark dark:hover:bg-surface-dark-secondary"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-border px-2 py-1 disabled:opacity-40 hover:bg-gray-50 dark:border-border-dark dark:hover:bg-surface-dark-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityIcon({ entityType, action }: { entityType: string; action: string }) {
  let color = 'text-gray-400';
  let icon = '●';

  if (entityType.includes('task')) {
    color = 'text-green-500';
    icon = '✓';
  } else if (entityType.includes('note')) {
    color = 'text-yellow-500';
    icon = '✎';
  } else if (entityType.includes('document')) {
    color = 'text-blue-500';
    icon = '📄';
  } else if (entityType.includes('job') && action === 'create') {
    color = 'text-brand-500';
    icon = '⊕';
  } else if (entityType.includes('job') && action === 'update') {
    color = 'text-purple-500';
    icon = '↻';
  }

  return (
    <span
      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs dark:bg-gray-700 ${color}`}
    >
      {icon}
    </span>
  );
}

/** Dynamic detail section — renders form fields in read-only mode */
function JobDynamicDetailSection({
  section,
  job,
}: {
  section: FormSection;
  job: import('./hooks/useJobs').JobDetail;
}) {
  const visibleFields = section.fields.filter((f) => f.is_active);
  if (visibleFields.length === 0) return null;

  const jobData = job as unknown as Record<string, unknown>;

  // Read from form_data JSONB (single source of truth)
  const formDataRaw = jobData.formData || jobData.form_data;
  const savedFormData: Record<string, unknown> =
    typeof formDataRaw === 'string'
      ? JSON.parse(formDataRaw)
      : ((formDataRaw as Record<string, unknown> | undefined) ?? {});

  const fieldValues = visibleFields.map((field) => {
    let value: unknown;
    if (field.is_system_field && field.db_column) {
      const camelKey = field.db_column.replace(/_([a-z])/g, (_: string, c: string) =>
        c.toUpperCase(),
      );
      if (field.db_column === 'is_recurring') {
        value = job.isRecurring;
      } else if (field.db_column === 'recurrence_frequency') {
        value = job.recurrenceFrequency;
      } else if (field.db_column === 'recurrence_next_date') {
        value = job.recurrenceNextDate;
      } else {
        value =
          savedFormData[field.db_column] ?? savedFormData[field.field_key] ?? jobData[camelKey];
      }
      // Format special values
      if (value && (field.db_column.includes('date') || field.db_column.includes('_at'))) {
        try {
          value = new Date(value as string).toLocaleDateString();
        } catch {
          /* keep raw */
        }
      }
      if (field.db_column === 'budget_amount' && value)
        value = `$${Number(value).toLocaleString()}`;
      if (field.db_column === 'estimated_hours' && value) value = `${value}h`;
      if (field.db_column === 'is_recurring') value = value ? 'Yes' : 'No';
      if (field.db_column === 'recurrence_frequency' && value) {
        value = String(value).charAt(0).toUpperCase() + String(value).slice(1);
      }
      // Name lookups for FK fields — show name instead of UUID
      if (field.db_column.endsWith('_id')) {
        const nameKey = field.db_column
          .replace('_id', '_name')
          .replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase());
        const resolvedName = jobData[nameKey];
        if (resolvedName) value = resolvedName;
      }
    } else {
      // Custom field — read from form_data
      value = savedFormData[field.field_key];
    }

    // Format arrays (table fields)
    if (Array.isArray(value)) {
      value = value
        .map((row: Record<string, unknown>) => Object.values(row).join(', '))
        .join(' | ');
    }

    return { label: field.label, value: value != null && value !== '' ? String(value) : '—' };
  });

  const hasRecurrenceField = visibleFields.some(
    (field) =>
      field.db_column === 'is_recurring' ||
      field.db_column === 'recurrence_frequency' ||
      field.field_key === 'is_recurring' ||
      field.field_key === 'recurrence_frequency',
  );
  const hasNextDateField = visibleFields.some(
    (field) =>
      field.db_column === 'recurrence_next_date' || field.field_key === 'recurrence_next_date',
  );
  if (hasRecurrenceField && !hasNextDateField) {
    const nextDate = job.recurrenceNextDate
      ? new Date(job.recurrenceNextDate).toLocaleDateString()
      : '—';
    fieldValues.push({ label: 'Next Date', value: nextDate });
  }

  return (
    <Card>
      <CardHeader title={section.name} />
      <CardContent>
        <dl className="space-y-3">
          {fieldValues.map((item, idx) => (
            <div key={idx} className="flex flex-col sm:flex-row sm:gap-4">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 sm:w-40 shrink-0">
                {item.label}
              </dt>
              <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100 sm:mt-0">
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

/** Job details panel (fallback when no form definition exists) */
function JobDetails({ job }: { job: import('./hooks/useJobs').JobDetail }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</h4>
        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
          {job.description || 'No description provided'}
        </p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Assigned Partner</h4>
        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
          {job.assignedPartnerName || '—'}
        </p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Assigned Manager</h4>
        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
          {job.assignedManagerName || '—'}
        </p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
          Assigned Accountant
        </h4>
        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
          {job.assignedAccountantName || '—'}
        </p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Budget</h4>
        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
          {job.budgetAmount ? `$${job.budgetAmount.toLocaleString()}` : '—'}
        </p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Estimated Hours</h4>
        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
          {job.estimatedHours ? `${job.estimatedHours}h` : '—'}
        </p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Start Date</h4>
        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
          {job.startDate ? new Date(job.startDate).toLocaleDateString() : '—'}
        </p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Recurring</h4>
        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
          {job.isRecurring ? `Yes (${job.recurrenceFrequency})` : 'No'}
        </p>
      </div>
      {job.isRecurring && (
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Next Date</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
            {job.recurrenceNextDate ? new Date(job.recurrenceNextDate).toLocaleDateString() : '—'}
          </p>
        </div>
      )}
      {job.cancellationReason && (
        <div className="sm:col-span-2">
          <h4 className="text-sm font-medium text-danger-500">Cancellation Reason</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{job.cancellationReason}</p>
        </div>
      )}
    </div>
  );
}

// ─── Job Notes Tab ────────────────────────────────────────────────────────────

function JobNotesTab({ jobId }: { jobId: string }) {
  const { can } = usePermissions();
  const { toast } = useToast();
  const { data: notes, isLoading } = useJobNotes(jobId);
  const { data: categories } = useNoteCategories();
  const { data: staffUsers } = useStaffUsers();
  const createNoteMutation = useCreateJobNote(jobId);

  const [showAddForm, setShowAddForm] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteCategory, setNoteCategory] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [reminderAssigneeId, setReminderAssigneeId] = useState('');
  const [reminderAssigneeSearch, setReminderAssigneeSearch] = useState('');
  const [showReminderAssigneeDropdown, setShowReminderAssigneeDropdown] = useState(false);
  const [clientVisible, setClientVisible] = useState(false);

  // Check if selected category is a scheduling category
  const selectedCategoryObj = categories?.find(
    (cat) => cat.name.toLowerCase().replace(/\s+/g, '_') === noteCategory,
  );
  const isSchedulingCategory = selectedCategoryObj?.isScheduling ?? false;

  const handleAddNote = useCallback(async () => {
    if (!noteContent.trim()) return;
    if (isSchedulingCategory && !reminderDate) {
      toast({ variant: 'error', title: 'Reminder date is required for this category' });
      return;
    }
    try {
      await createNoteMutation.mutateAsync({
        content: noteContent.trim(),
        category: noteCategory || null,
        reminderDate: isSchedulingCategory && reminderDate ? reminderDate : undefined,
        reminderAssigneeId:
          isSchedulingCategory && reminderAssigneeId ? reminderAssigneeId : undefined,
        clientVisible,
      });
      setNoteContent('');
      setNoteCategory('');
      setReminderDate('');
      setReminderAssigneeId('');
      setClientVisible(false);
      setReminderAssigneeSearch('');
      setShowAddForm(false);
      toast({ variant: 'success', title: 'Note added' });
    } catch {
      toast({ variant: 'error', title: 'Failed to add note' });
    }
  }, [
    noteContent,
    noteCategory,
    reminderDate,
    reminderAssigneeId,
    isSchedulingCategory,
    clientVisible,
    createNoteMutation,
    toast,
  ]);

  return (
    <Card>
      <CardHeader
        title="Notes"
        action={
          can(PERMISSIONS.NOTE_CATEGORIES_CREATE) ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? 'Cancel' : 'Add Note'}
            </Button>
          ) : undefined
        }
      />
      <CardContent>
        {showAddForm && (
          <div className="mb-4 rounded-lg border border-border p-4 dark:border-border-dark space-y-3">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Category
              </label>
              <select
                value={noteCategory}
                onChange={(e) => setNoteCategory(e.target.value)}
                className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="">Select category</option>
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.name.toLowerCase().replace(/\s+/g, '_')}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Content <span className="text-red-500">*</span>
              </label>
              <textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                rows={4}
                maxLength={10000}
                placeholder="Type your note here..."
                className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            {isSchedulingCategory && (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Reminder Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={reminderDate}
                  onChange={(e) => setReminderDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                />
                <p className="mt-1 text-xs text-gray-500">
                  A reminder will be created and triggered on this date.
                </p>
              </div>
            )}
            {isSchedulingCategory && (
              <div className="relative">
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Assign Reminder To
                </label>
                <input
                  type="text"
                  value={reminderAssigneeSearch}
                  onChange={(e) => {
                    setReminderAssigneeSearch(e.target.value);
                    setShowReminderAssigneeDropdown(true);
                    if (!e.target.value.trim()) setReminderAssigneeId('');
                  }}
                  onFocus={() => setShowReminderAssigneeDropdown(true)}
                  onBlur={() => setTimeout(() => setShowReminderAssigneeDropdown(false), 200)}
                  placeholder="Search user (leave empty for yourself)..."
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
                  autoComplete="off"
                />
                {showReminderAssigneeDropdown && staffUsers && staffUsers.length > 0 && (
                  <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-border bg-white shadow-lg dark:border-border-dark dark:bg-gray-800">
                    {(staffUsers ?? [])
                      .filter((u) => {
                        if (!reminderAssigneeSearch.trim()) return true;
                        const q = reminderAssigneeSearch.toLowerCase();
                        return (
                          u.email.toLowerCase().includes(q) || u.name.toLowerCase().includes(q)
                        );
                      })
                      .slice(0, 20)
                      .map((user) => (
                        <li key={user.id}>
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setReminderAssigneeId(user.id);
                              setReminderAssigneeSearch(user.name || user.email);
                              setShowReminderAssigneeDropdown(false);
                            }}
                          >
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {user.name || user.email}
                            </span>
                            {user.name && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {user.email}
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                  </ul>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Optional. If left empty, the reminder will be assigned to you.
                </p>
              </div>
            )}
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={clientVisible}
                onChange={(e) => setClientVisible(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
              />
              <span>
                Visible to client
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  Shares this note in the client portal and emails the client (if the message
                  template is active).
                </span>
              </span>
            </label>
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                onClick={handleAddNote}
                loading={createNoteMutation.isPending}
                disabled={!noteContent.trim()}
              >
                Save Note
              </Button>
            </div>
          </div>
        )}
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading notes...</p>
        ) : notes && notes.length > 0 ? (
          <ul role="list" className="divide-y divide-border dark:divide-border-dark">
            {notes.map((note) => (
              <li key={note.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {note.category && (
                        <Badge variant="default" size="sm">
                          {note.category.replace(/_/g, ' ')}
                        </Badge>
                      )}
                      {note.createdByName && (
                        <span className="text-xs text-gray-500">by {note.createdByName}</span>
                      )}
                      {note.senderType === 'client' ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={1.5}
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                            />
                          </svg>
                          From client
                        </span>
                      ) : note.clientVisible ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={1.5}
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          Visible to client
                        </span>
                      ) : null}
                    </div>
                    <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                      {note.content}
                    </p>
                  </div>
                  <time className="shrink-0 text-xs text-gray-400">
                    {new Date(note.createdAt).toLocaleDateString('en-CA', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </time>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">
            No notes yet. Add a note to keep track of job-related interactions.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Assign-to-Client Button ─────────────────────────────────────────────────

/**
 * Toggle button to assign/unassign a task to the client, with a busy spinner.
 *
 * When assigning, a small popover lets staff optionally set a client due date
 * (drives overdue detection in the portal). Unassigning is a single click.
 * Uses clear primary/outlined styling (not a muted gray that reads as disabled).
 */
function AssignClientButton({
  assigned,
  busy,
  currentDueDate,
  onAssign,
}: {
  assigned: boolean;
  busy: boolean;
  /** Existing client due date (ISO), if any — prefills the picker. */
  currentDueDate?: string | null;
  /** Called with the new assignment state and (when assigning) an optional due date. */
  onAssign: (assigned: boolean, dueDate?: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  // Prefill the picker with the current due date (YYYY-MM-DD) when opening.
  useEffect(() => {
    if (open) {
      setDueDate(currentDueDate ? currentDueDate.slice(0, 10) : '');
    }
  }, [open, currentDueDate]);

  // Close the popover on outside click.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function handleClick() {
    if (assigned) {
      // Unassign immediately — no date needed.
      onAssign(false);
    } else {
      setOpen((v) => !v);
    }
  }

  function confirmAssign() {
    // Send an ISO datetime (end of the chosen day) or null for "no due date".
    const iso = dueDate ? new Date(`${dueDate}T23:59:59`).toISOString() : null;
    setOpen(false);
    onAssign(true, iso);
  }

  return (
    <div ref={popoverRef} className="relative inline-block">
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className={`inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-70 ${
          assigned
            ? 'border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50'
            : 'border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100 dark:border-brand-700 dark:bg-brand-900/30 dark:text-brand-300 dark:hover:bg-brand-900/50'
        }`}
        title={assigned ? 'Remove client assignment' : 'Assign to client'}
      >
        {busy ? (
          <>
            <svg
              className="h-3.5 w-3.5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            {assigned ? 'Unassigning…' : 'Assigning…'}
          </>
        ) : (
          <>
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
              />
            </svg>
            {assigned ? 'Unassign Client' : 'Assign to Client'}
          </>
        )}
      </button>

      {open && !assigned && (
        <div className="absolute right-0 z-20 mt-1 w-60 rounded-lg border border-border bg-white p-3 shadow-medium dark:border-border-dark dark:bg-surface-dark-secondary">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            Due date (optional)
          </label>
          <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
            The client sees this as the deadline; overdue items are flagged automatically.
          </p>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="mt-2 w-full rounded-md border border-border bg-white px-2 py-1 text-xs text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark dark:text-gray-100"
          />
          <div className="mt-3 flex items-center justify-between gap-2">
            {dueDate ? (
              <button
                type="button"
                onClick={() => setDueDate('')}
                className="text-[11px] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Clear
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmAssign}
                className="rounded-md bg-brand-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-700"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Document Upload Task Component ──────────────────────────────────────────

function DocumentUploadTask({
  task,
  jobId,
  disabled,
  onCompleted,
  isAdmin,
  canAssignToClient,
  onAssignToClient,
  assigning,
}: {
  task: JobTask;
  jobId: string;
  disabled: boolean;
  onCompleted: () => void;
  isAdmin: boolean;
  canAssignToClient?: boolean;
  onAssignToClient?: (taskId: string, assigned: boolean, dueDate?: string | null) => void;
  assigning?: boolean;
}) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>, isReplace = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('job_id', jobId);

      const uploadResponse = await apiClient.post<ApiResponse<{ id: string; file_name: string }>>(
        '/documents/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      const documentId = uploadResponse.data.data.id;

      // Update task with document_id
      await apiClient.patch(`/jobs/${jobId}/tasks/${task.id}`, { document_id: documentId });

      // Mark task as completed (only on initial upload, not replace)
      if (!isReplace && !task.isCompleted) {
        onCompleted();
      }

      // Invalidate job detail to refresh task data
      queryClient.invalidateQueries({ queryKey: ['jobs', 'detail'] });

      toast({
        variant: 'success',
        title: isReplace ? 'Document replaced successfully' : `Document "${file.name}" uploaded`,
      });
    } catch {
      toast({ variant: 'error', title: 'Failed to upload document' });
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handlePreview = async () => {
    if (!task.documentId) return;
    try {
      const response = await apiClient.get(`/documents/${task.documentId}/preview`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data as BlobPart], { type: 'application/octet-stream' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      toast({ variant: 'error', title: 'Failed to preview document' });
    }
  };

  const handleDownload = async () => {
    if (!task.documentId) return;
    try {
      const response = await apiClient.get(`/documents/${task.documentId}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data as BlobPart]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = task.title || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ variant: 'error', title: 'Failed to download document' });
    }
  };

  // Task completed with document attached — show view/download/replace
  if (task.isCompleted && task.documentId) {
    return (
      <div className="flex flex-1 items-center gap-3">
        <svg
          className="h-5 w-5 text-success-500 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="flex-1">
          <span className="text-sm text-gray-400 line-through dark:text-gray-500">
            {task.title}
            {task.isMandatory && <span className="ml-1 text-xs text-danger-500">*</span>}
          </span>
          <p className="text-xs text-success-600 dark:text-success-400">Document uploaded</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handlePreview}
            className="rounded p-1.5 text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
            title="Preview document"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="rounded p-1.5 text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
            title="Download document"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
              />
            </svg>
          </button>
          {isAdmin && (
            <>
              <input
                type="file"
                onChange={(e) => handleUpload(e, true)}
                className="hidden"
                id={`replace-${task.id}`}
                disabled={uploading}
              />
              <label
                htmlFor={`replace-${task.id}`}
                className="cursor-pointer rounded p-1.5 text-gray-400 hover:text-amber-600 dark:hover:text-amber-400"
                title="Replace document (admin only)"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                  />
                </svg>
              </label>
            </>
          )}
        </div>
      </div>
    );
  }

  // Task not completed — show upload button
  return (
    <div className="flex flex-1 items-center gap-3">
      <svg
        className="h-5 w-5 text-brand-500 flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
        />
      </svg>
      <div className="flex-1">
        <span className="text-sm text-gray-900 dark:text-gray-100">
          {task.title}
          {task.isMandatory && (
            <span className="ml-1 text-xs text-danger-500" aria-label="mandatory">
              *
            </span>
          )}
          {task.assignedToClient && (
            <Badge variant="warning" size="sm" className="ml-2">
              Waiting on Client
            </Badge>
          )}
        </span>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Upload a document to complete this task
        </p>
        {task.assigneeRole && (
          <span className="text-xs text-gray-400 dark:text-gray-500">({task.assigneeRole})</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {/* Assign the upload task to the client (they upload from the portal) */}
        {canAssignToClient && onAssignToClient && (
          <AssignClientButton
            assigned={task.assignedToClient}
            busy={!!assigning}
            currentDueDate={task.clientDueDate}
            onAssign={(assigned, dueDate) => onAssignToClient(task.id, assigned, dueDate)}
          />
        )}
        <input
          type="file"
          onChange={(e) => handleUpload(e)}
          className="hidden"
          id={`upload-${task.id}`}
          disabled={disabled || uploading}
        />
        <label
          htmlFor={`upload-${task.id}`}
          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            disabled || uploading
              ? 'cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
              : 'bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-300 dark:hover:bg-brand-900/50'
          }`}
        >
          {uploading ? 'Uploading...' : 'Upload Document'}
        </label>
      </div>
    </div>
  );
}
